import type { Participant } from '../../interfaces/Participant';
import { dotvotingApi } from '../../services/dotvotingApi';
import { ApiError } from '../../services/apiClient';
import { sanitizeBoardColumnTitle } from '../../utils/boardColumns';
import { isTeamsHost } from '../../services/webAuth';
import useUserStore from '../userStore';
import {
  applyParticipantDeletionToState,
  countVotesForOwners,
  getEquivalentParticipantIds,
  getParticipantNameKey,
  mapBoardParticipants,
  normalizeError,
  publishBoardChange,
  sanitizeBoardDetails,
  syncViewCard,
} from './helpers';
import type { BoardState, BoardStoreGet, BoardStoreSet } from './types';
import { trackAnalyticsOnce, trackAnalyticsOncePerSession } from '@dotstorming/analytics';

export const createBoardActions = (
  set: BoardStoreSet,
  get: BoardStoreGet
): Pick<
  BoardState,
  | 'setBoardId'
  | 'clearActionError'
  | 'createBoard'
  | 'loadBoard'
  | 'joinBoardAsParticipant'
  | 'removeParticipant'
  | 'updateBoardDetails'
  | 'updateBoardColumn'
  | 'updateBoardColumnTitle'
  | 'setPrivateVotingEnabled'
  | 'setVotingEnabled'
  | 'setCommentingEnabled'
  | 'setChatEnabled'
  | 'setParticipantsEnabled'
  | 'setGiphyEnabled'
  | 'setShowVoterNames'
  | 'setParticipantNameVisibility'
  | 'setBoardLocked'
  | 'startBoardTimer'
  | 'stopBoardTimer'
  | 'resumeBoardTimer'
  | 'resetBoardTimer'
  | 'completeBoardTimer'
  | 'getBoardBackground'
  | 'setBoardBackground'
  | 'exportBoard'
  | 'saveBoardTemplate'
  | 'getBoardQrCode'
  | 'sendInvitations'
  | 'sendFeedback'
  | 'getImportableDotstormingBoards'
  | 'getImportableDotstormingCards'
  | 'importTxtCsvCards'
  | 'importDotstormingCards'
  | 'clearBoard'
  | 'getTotalVotesByOwner'
  | 'hasExceededVoteLimit'
> => ({
  setBoardId: (boardId) => set({ boardId }),
  clearActionError: () => set({ actionError: null }),
  createBoard: async ({
    authToken,
    title,
    description = '',
    source = 'default',
    layout = 'lanes',
    columns = [],
    cardSorting = 'automatic',
    votingStyle = 'dotvoting',
    privateVotingEnabled = false,
    votingEnabled = true,
    votesPerUser = 3,
    participantUploadsEnabled = true,
    timerEnabled = true,
  }) => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      set({ actionError: 'Title is required.' });
      return null;
    }

    try {
      const topic = await dotvotingApi.createBoard(authToken, {
        title: trimmedTitle,
        description,
        isPublic: true,
        source,
        layout,
        columns,
        cardSorting,
        votingStyle,
        privateVotingEnabled,
        votingEnabled,
        votesPerUser,
        participantUploadsEnabled,
        timerEnabled,
        uploadTypes: 'text,image',
        participants: [],
      });

      set({ actionError: null });
      return topic?._id ?? null;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return null;
    }
  },
  loadBoard: async (boardId, options) => {
    set({ isLoadingBoard: true, boardLoadError: null, actionError: null });

    try {
      dotvotingApi.setSession(boardId, options);

      const board = await dotvotingApi.getBoard(boardId);
      const session = useUserStore.getState().getBoardSession(boardId);
      const sessionUser = session.user;
      const member = await dotvotingApi.ensureMember(boardId, board.participants);

      if (member) {
        useUserStore.getState().setCurrentUser(
          {
            id: member._id,
            name: member.name,
            votes: 0,
          },
          { isGuest: false }
        );
      } else if (!sessionUser) {
        useUserStore.getState().setCurrentUser(null);
      } else if (!isTeamsHost()) {
        const existingParticipant =
          board.participants.find((participant) => participant._id === sessionUser.id) ?? null;

        if (existingParticipant) {
          useUserStore.getState().setCurrentUser(
            {
              id: existingParticipant._id,
              name: existingParticipant.name,
              votes: 0,
            },
            { isGuest: session.isGuest }
          );
        } else {
          useUserStore.getState().setCurrentUser(null);
        }
      }

      const boardDetails = sanitizeBoardDetails(board);

      if (boardDetails.isOwner) {
        const createdAt = new Date(boardDetails.created).getTime();
        const ageInDays = Math.max(0, Math.floor((Date.now() - createdAt) / 86_400_000));
        if (Number.isFinite(createdAt) && Date.now() - createdAt > 30 * 60 * 1000) {
          trackAnalyticsOncePerSession(`board_reopened_${boardId}`, 'board_reopened', {
            open_source: 'direct',
            days_since_created_bucket:
              ageInDays < 1 ? '<1' : ageInDays < 7 ? '1-6' : ageInDays < 30 ? '7-29' : '30+',
          });
        }
      }

      set((state) => ({
        boardId,
        boardDetails,
        participants: mapBoardParticipants(boardDetails),
        chat: board.chat ?? [],
        ideas: board.ideas,
        viewCard: syncViewCard(board.ideas, state.viewCard),
        isLoadingBoard: false,
        boardLoadError: null,
        showVoteOwners: false,
      }));
    } catch (error) {
      useUserStore.getState().setCurrentUser(null);

      set({
        boardDetails: null,
        participants: [],
        chat: [],
        ideas: [],
        viewCard: null,
        isLoadingBoard: false,
        boardLoadError: normalizeError(error),
        showVoteOwners: false,
      });
    }
  },
  joinBoardAsParticipant: async (name) => {
    const { boardId } = get();
    const trimmedName = name.trim();

    if (!boardId) {
      return 'Board not loaded.';
    }

    if (!trimmedName) {
      return 'Please enter your name.';
    }

    try {
      const member = await dotvotingApi.joinMember(boardId, trimmedName);
      const joinedParticipant: Participant = {
        id: member._id,
        name: member.name,
        votes: 0,
      };

      useUserStore.getState().setCurrentUser(joinedParticipant, {
        isGuest: true,
      });

      set((state) => ({
        participants: state.participants.some(
          (existingParticipant) => existingParticipant.id === joinedParticipant.id
        )
          ? state.participants
          : [...state.participants, joinedParticipant].sort((left, right) =>
              left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
            ),
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicParticipantAdded', {
        participant: joinedParticipant,
      });
      trackAnalyticsOnce(`participant_joined_${boardId}`, 'participant_joined', {
        participant_type: 'guest',
        join_source: 'board',
      });

      return null;
    } catch (error) {
      const message = normalizeError(error);
      set({ actionError: message });
      return message;
    }
  },
  removeParticipant: async (participant) => {
    const { boardId, boardDetails, participants } = get();
    if (!boardId || !boardDetails?.isOwner) {
      return;
    }

    const currentUserId = useUserStore.getState().getCurrentUser()?.id?.trim();
    if (!currentUserId) {
      return;
    }

    const participantId = participant.id.trim();
    const participantNameKey = getParticipantNameKey(participant.name);

    const participantsToDelete = participants.filter((existingParticipant) => {
      if (existingParticipant.id === currentUserId) {
        return false;
      }

      return (
        existingParticipant.id === participantId ||
        getParticipantNameKey(existingParticipant.name) === participantNameKey
      );
    });

    if (participantsToDelete.length === 0) {
      return;
    }

    const participantIdsToDelete = Array.from(
      new Set(participantsToDelete.map((existingParticipant) => existingParticipant.id))
    );

    try {
      await dotvotingApi.deleteMember(boardId, participantIdsToDelete.join(','));

      const removedParticipantIds = new Set(participantIdsToDelete);
      const removedParticipantNameKeys = new Set(
        participantsToDelete.map((existingParticipant) =>
          getParticipantNameKey(existingParticipant.name)
        )
      );

      set((state) => ({
        ...applyParticipantDeletionToState(
          state,
          removedParticipantIds,
          removedParticipantNameKeys
        ),
        actionError: null,
      }));

      participantsToDelete.forEach((participantToDelete) => {
        publishBoardChange(boardId, 'topicParticipantDeleted', {
          participant: {
            id: participantToDelete.id,
            name: participantToDelete.name,
          },
        });
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  updateBoardDetails: async (updates) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    const nextBoardDetails = sanitizeBoardDetails({ ...boardDetails, ...updates });

    try {
      const savedBoard = await dotvotingApi.updateBoard(boardId, nextBoardDetails);
      const sanitizedSavedBoard = sanitizeBoardDetails(savedBoard);

      set((state) => ({
        boardDetails: sanitizedSavedBoard,
        participants: mapBoardParticipants(sanitizedSavedBoard),
        actionError: null,
        viewCard: syncViewCard(state.ideas, state.viewCard),
      }));
      publishBoardChange(boardId, 'topicUpdated', {
        updates: {
          title: sanitizedSavedBoard.title,
          description: sanitizedSavedBoard.description,
          layout: sanitizedSavedBoard.layout,
          columns: sanitizedSavedBoard.columns,
          cardSorting: sanitizedSavedBoard.cardSorting,
          votingStyle: sanitizedSavedBoard.votingStyle,
          votingEnabled: sanitizedSavedBoard.votingEnabled,
          privateVotingEnabled: sanitizedSavedBoard.privateVotingEnabled,
          votesPerUser: sanitizedSavedBoard.votesPerUser,
          commentsEnabled: sanitizedSavedBoard.commentsEnabled,
          giphyEnabled: sanitizedSavedBoard.giphyEnabled,
          randomizeCardPosition: sanitizedSavedBoard.randomizeCardPosition,
          viewParticipantNames: sanitizedSavedBoard.viewParticipantNames,
          allViewParticipantNames: sanitizedSavedBoard.allViewParticipantNames,
          chatEnabled: sanitizedSavedBoard.chatEnabled,
          participantUploadsEnabled: sanitizedSavedBoard.participantUploadsEnabled,
          timerEnabled: sanitizedSavedBoard.timerEnabled,
          uploadTypes: sanitizedSavedBoard.uploadTypes,
          logoVisible: sanitizedSavedBoard.logoVisible,
          attachmentOptional: sanitizedSavedBoard.attachmentOptional,
          participants: sanitizedSavedBoard.participants,
        },
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
      throw error;
    }
  },
  updateBoardColumn: async (columnId, updates) => {
    const { boardId, boardDetails } = get();
    if (
      !boardId ||
      !boardDetails?.isOwner ||
      boardDetails.locked ||
      boardDetails.layout !== 'columns'
    ) {
      return;
    }

    const sanitizedTitle =
      typeof updates.title === 'string' ? sanitizeBoardColumnTitle(updates.title) : undefined;
    if (!columnId.trim() || (typeof updates.title === 'string' && !sanitizedTitle)) {
      return;
    }

    const existingColumns = boardDetails.columns ?? [];
    const columnExists = existingColumns.some((column) => column.id === columnId);
    if (!columnExists) {
      return;
    }

    const nextColumns = existingColumns.map((column) => {
      if (column.id !== columnId) {
        return column;
      }

      return {
        ...column,
        ...(sanitizedTitle ? { title: sanitizedTitle } : {}),
        ...(typeof updates.allowCardCreation === 'boolean'
          ? { allowCardCreation: updates.allowCardCreation }
          : {}),
      };
    });
    const nextBoardDetails = sanitizeBoardDetails({
      ...boardDetails,
      columns: nextColumns,
    });
    const previousBoardDetails = boardDetails;

    set({
      boardDetails: nextBoardDetails,
      actionError: null,
    });

    try {
      const savedBoard = await dotvotingApi.updateBoard(boardId, nextBoardDetails);
      const sanitizedSavedBoard = sanitizeBoardDetails(savedBoard);

      set((state) => ({
        boardDetails: sanitizedSavedBoard,
        participants: mapBoardParticipants(sanitizedSavedBoard),
        actionError: null,
        viewCard: syncViewCard(state.ideas, state.viewCard),
      }));
      publishBoardChange(boardId, 'topicUpdated', {
        updates: {
          columns: sanitizedSavedBoard.columns,
        },
      });
    } catch (error) {
      set({
        boardDetails: previousBoardDetails,
        actionError: normalizeError(error),
      });
    }
  },
  updateBoardColumnTitle: async (columnId, title) => {
    await get().updateBoardColumn(columnId, { title });
  },
  setPrivateVotingEnabled: async (enabled) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails || boardDetails.votingStyle !== 'dotvoting') {
      return;
    }

    try {
      await dotvotingApi.setPrivateVotingEnabled(boardId, enabled);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            privateVotingEnabled: enabled,
          }),
          actionError: null,
        };
      });
      publishBoardChange(
        boardId,
        enabled ? 'topicPrivateVotingEnabled' : 'topicPrivateVotingDisabled'
      );
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setVotingEnabled: async (enabled) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails || boardDetails.votingStyle !== 'dotvoting') {
      return;
    }

    try {
      await dotvotingApi.setVotingEnabled(boardId, enabled);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            votingEnabled: enabled,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, enabled ? 'topicVotingEnabled' : 'topicVotingDisabled');
      if (enabled && !boardDetails.votingEnabled) {
        trackAnalyticsOnce(`vote_started_${boardId}`, 'vote_started', {
          voting_style: boardDetails.votingStyle,
        });
      }
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setCommentingEnabled: async (enabled) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    try {
      await dotvotingApi.setCommentingEnabled(boardId, enabled);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            commentsEnabled: enabled,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, enabled ? 'topicCommentingEnabled' : 'topicCommentingDisabled');
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setChatEnabled: async (enabled) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    try {
      await dotvotingApi.setChatEnabled(boardId, enabled);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            chatEnabled: enabled,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, enabled ? 'topicChatEnabled' : 'topicChatDisabled');
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setParticipantsEnabled: async (enabled) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    try {
      await dotvotingApi.setParticipantsEnabled(boardId, enabled);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            participantsEnabled: enabled,
          }),
          actionError: null,
        };
      });
      publishBoardChange(
        boardId,
        enabled ? 'topicParticipantsEnabled' : 'topicParticipantsDisabled'
      );
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setGiphyEnabled: async (enabled) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    try {
      await dotvotingApi.setGiphyEnabled(boardId, enabled);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            giphyEnabled: enabled,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicGiphyEnabled', { enabled });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setShowVoterNames: (enabled) => set({ showVoteOwners: enabled }),
  setParticipantNameVisibility: async (mode) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    const status = mode === 'everyone' ? 'all' : mode === 'owner-only' ? 'owner' : 'none';
    const viewParticipantNames = mode === 'owner-only';
    const allViewParticipantNames = mode === 'everyone';

    try {
      await dotvotingApi.setParticipantNameVisibility(boardId, status);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            viewParticipantNames,
            allViewParticipantNames,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicUpdated', {
        updates: {
          viewParticipantNames,
          allViewParticipantNames,
        },
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setBoardLocked: async (locked) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    try {
      await dotvotingApi.setLocked(boardId, locked);
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            locked,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, locked ? 'topicIdeasLocked' : 'topicIdeasUnlocked');
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  startBoardTimer: async (durationSeconds, completionActions) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails?.isOwner) {
      return;
    }

    try {
      const timerUpdates = await dotvotingApi.setBoardTimer(
        boardId,
        'start',
        durationSeconds,
        completionActions
      );
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            ...timerUpdates,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicUpdated', {
        updates: timerUpdates,
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  stopBoardTimer: async () => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails?.isOwner) {
      return;
    }

    try {
      const timerUpdates = await dotvotingApi.setBoardTimer(boardId, 'stop');
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            ...timerUpdates,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicUpdated', {
        updates: timerUpdates,
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  resumeBoardTimer: async () => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails?.isOwner) {
      return;
    }

    try {
      const timerUpdates = await dotvotingApi.setBoardTimer(boardId, 'resume');
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            ...timerUpdates,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicUpdated', {
        updates: timerUpdates,
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  resetBoardTimer: async () => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails?.isOwner) {
      return;
    }

    try {
      const timerUpdates = await dotvotingApi.setBoardTimer(boardId, 'reset');
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            ...timerUpdates,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicUpdated', {
        updates: timerUpdates,
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  completeBoardTimer: async () => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails) {
      return;
    }

    try {
      const timerUpdates = await dotvotingApi.setBoardTimer(boardId, 'complete');
      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            ...timerUpdates,
          }),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicUpdated', {
        updates: timerUpdates,
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  getBoardBackground: async () => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails?.background?.image?.trim()) {
      return null;
    }

    try {
      const backgroundUrl = await dotvotingApi.getBoardBackground(boardId);
      set({ actionError: null });
      return backgroundUrl;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return null;
    }
  },
  setBoardBackground: async (file, repeat) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails || !boardDetails.isOwner) {
      return false;
    }

    try {
      await dotvotingApi.setBoardBackground(boardId, file);
      const background = await dotvotingApi.setBoardBackgroundRepeat(boardId, repeat);

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            background,
          }),
          actionError: null,
        };
      });

      return true;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return false;
    }
  },
  exportBoard: async () => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails || !boardDetails.isOwner) {
      return;
    }

    try {
      const { blob, filename } = await dotvotingApi.exportBoard(boardId, boardDetails.owner);

      if (typeof window !== 'undefined') {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      set({ actionError: null });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  saveBoardTemplate: async (input) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails || !boardDetails.isOwner) {
      return false;
    }

    try {
      await dotvotingApi.saveBoardTemplate(boardId, input);
      set({ actionError: null });
      return true;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return false;
    }
  },
  getBoardQrCode: async (boardId) => {
    const trimmedBoardId = boardId.trim();
    if (!trimmedBoardId) {
      return null;
    }

    try {
      const qrCodeDataUrl = await dotvotingApi.getBoardQrCode(trimmedBoardId);
      set({ actionError: null });
      return qrCodeDataUrl;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return null;
    }
  },
  sendInvitations: async (emailAddresses, emailMessage) => {
    const { boardId, boardDetails } = get();
    const trimmedEmailAddresses = emailAddresses.trim();

    if (!boardId || !boardDetails || !boardDetails.isOwner) {
      return { sent: false };
    }

    if (!trimmedEmailAddresses) {
      set({ actionError: 'Please enter at least one email address.' });
      return { sent: false, message: 'Please enter at least one email address.' };
    }

    try {
      const response = await dotvotingApi.sendInvitations(
        boardId,
        trimmedEmailAddresses,
        emailMessage
      );
      set({ actionError: null });
      return { sent: true, rateLimit: response.rateLimit };
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return {
        sent: false,
        code:
          error instanceof ApiError && error.status === 429
            ? 'INVITATION_RATE_LIMITED'
            : error instanceof ApiError && error.status === 403
            ? 'OUTBOUND_EMAIL_BLOCKED'
            : undefined,
        message: normalizeError(error),
      };
    }
  },
  sendFeedback: async (comment) => {
    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      set({ actionError: 'Please enter your comment.' });
      return false;
    }

    try {
      await dotvotingApi.sendFeedback(trimmedComment, 'board');
      set({ actionError: null });
      return true;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return false;
    }
  },
  getImportableDotstormingBoards: async () => {
    try {
      const boards = await dotvotingApi.getImportableDotstormingBoards();
      set({ actionError: null });
      return boards;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return [];
    }
  },
  getImportableDotstormingCards: async (sourceBoardId) => {
    const trimmedSourceBoardId = sourceBoardId.trim();
    if (!trimmedSourceBoardId) {
      return [];
    }

    try {
      const cards = await dotvotingApi.getImportableDotstormingCards(trimmedSourceBoardId);
      set({ actionError: null });
      return cards;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return [];
    }
  },
  importTxtCsvCards: async (file) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails || !boardDetails.isOwner) {
      return false;
    }

    try {
      await dotvotingApi.importTxtCsvCards(boardId, file);
      const board = await dotvotingApi.getBoard(boardId);
      const sanitizedBoard = sanitizeBoardDetails(board);

      set((state) => ({
        boardDetails: sanitizedBoard,
        participants: mapBoardParticipants(sanitizedBoard),
        chat: board.chat ?? [],
        ideas: board.ideas,
        viewCard: syncViewCard(board.ideas, state.viewCard),
        actionError: null,
      }));
      return true;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return false;
    }
  },
  importDotstormingCards: async (sourceBoardId, cardIds) => {
    const { boardId, boardDetails } = get();
    const trimmedSourceBoardId = sourceBoardId.trim();
    if (!boardId || !boardDetails || !boardDetails.isOwner || !trimmedSourceBoardId) {
      return false;
    }

    try {
      await dotvotingApi.importDotstormingCards(boardId, trimmedSourceBoardId, cardIds);
      const board = await dotvotingApi.getBoard(boardId);
      const sanitizedBoard = sanitizeBoardDetails(board);

      set((state) => ({
        boardDetails: sanitizedBoard,
        participants: mapBoardParticipants(sanitizedBoard),
        chat: board.chat ?? [],
        ideas: board.ideas,
        viewCard: syncViewCard(board.ideas, state.viewCard),
        actionError: null,
      }));
      return true;
    } catch (error) {
      set({ actionError: normalizeError(error) });
      return false;
    }
  },
  clearBoard: async (mode) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails || !boardDetails.isOwner) {
      return;
    }

    try {
      if (mode === 'votes') {
        await dotvotingApi.clearVotes(boardId);
      } else {
        await dotvotingApi.clearAll(boardId);
      }

      publishBoardChange(boardId, mode === 'votes' ? 'votesCleared' : 'boardCleared');

      const board = await dotvotingApi.getBoard(boardId);
      const sanitizedBoard = sanitizeBoardDetails(board);

      set((state) => ({
        boardDetails: sanitizedBoard,
        participants: mapBoardParticipants(sanitizedBoard),
        chat: board.chat ?? [],
        ideas: board.ideas,
        viewCard: syncViewCard(board.ideas, state.viewCard),
        actionError: null,
      }));
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  getTotalVotesByOwner: (ownerId) => {
    const { ideas, participants } = get();
    const equivalentParticipantIds = getEquivalentParticipantIds(participants, ownerId);
    return countVotesForOwners(ideas, equivalentParticipantIds);
  },
  hasExceededVoteLimit: (ownerId) => {
    const { boardDetails, ideas, participants } = get();
    if (!boardDetails?.votingEnabled || boardDetails.votingStyle !== 'dotvoting') {
      return false;
    }
    const limit = boardDetails.votesPerUser ?? 0;
    if (limit <= 0) {
      return false;
    }

    const equivalentParticipantIds = getEquivalentParticipantIds(participants, ownerId);
    return countVotesForOwners(ideas, equivalentParticipantIds) >= limit;
  },
});
